import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Copy, Check, Gift, Users, Trophy, Coins, Share2, Target, Star, Medal, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const TIER_ICONS = { bronze: Medal, silver: Medal, gold: Crown, platinum: Star, diamond: Star };
const TIER_EMOJI = { bronze: '', silver: '', gold: '', platinum: '', diamond: '' };

export default function ReferralPage() {
  const { token, API, user } = useAuth();
  const navigate = useNavigate();
  const [myCode, setMyCode] = useState('');
  const [stats, setStats] = useState({});
  const [inputCode, setInputCode] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [currentTier, setCurrentTier] = useState(null);
  const [nextTier, setNextTier] = useState(null);
  const [referredCount, setReferredCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [refRes, lbRes, msRes] = await Promise.all([
          axios.get(`${API}/referral/my-code`, { headers, withCredentials: true }),
          axios.get(`${API}/referral/leaderboard`, { headers, withCredentials: true }),
          axios.get(`${API}/referral/milestones`, { headers, withCredentials: true }),
        ]);
        const ref = refRes.data.referral;
        setMyCode(ref.code);
        setStats(ref);
        setLeaderboard(lbRes.data.leaderboard || []);
        setMilestones(msRes.data.milestones || []);
        setCurrentTier(msRes.data.current_tier);
        setNextTier(msRes.data.next_tier);
        setReferredCount(msRes.data.referred_count || 0);
      } catch (e) { console.error(e); }
    };
    if (token) fetchAll();
  }, [API, token]);

  const copyCode = () => {
    navigator.clipboard.writeText(myCode);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareCode = () => {
    const text = `Hey! Join MathTopper - India's best daily math app. Use my code ${myCode} to get 50 FREE MathCoins! Download now.`;
    if (navigator.share) {
      navigator.share({ title: 'MathTopper Referral', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Share message copied to clipboard!');
    }
  };

  const applyCode = async () => {
    if (!inputCode.trim()) { toast.error('Enter a code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral/apply`, { code: inputCode.trim().toUpperCase() }, { headers, withCredentials: true });
      toast.success(res.data.message);
      setInputCode('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const progressToNext = nextTier ? Math.min(100, (referredCount / nextTier.required) * 100) : 100;

  return (
    <div className="min-h-screen pb-20" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="referral-back-btn"><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <h1 className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>Refer & Earn</h1>
      </div>

      <div className="px-4 space-y-4">
        {/* Your Code + Share */}
        <div className="bg-gradient-to-br from-[#FF6B35] to-[#FFD23F] rounded-2xl p-5 text-center" data-testid="referral-code-section">
          <Gift size={36} className="mx-auto text-white mb-2" />
          <p className="text-white font-bold text-sm mb-1">Share your code & earn 100 MathCoins per friend!</p>
          <div className="bg-white/90 rounded-xl p-3 flex items-center justify-center gap-3 mt-3">
            <p className="text-xl font-black tracking-widest text-[#1B4F8A]" data-testid="my-referral-code">{myCode}</p>
            <button onClick={copyCode} className="p-2 rounded-full bg-[#FF6B35]/10" data-testid="copy-referral-code">
              {copied ? <Check size={18} color="#06D6A0" /> : <Copy size={18} color="#FF6B35" />}
            </button>
          </div>
          <Button onClick={shareCode} data-testid="share-referral-btn"
            className="mt-3 bg-white/20 hover:bg-white/30 text-white font-bold rounded-full px-6 text-xs border border-white/30">
            <Share2 size={14} className="mr-1" /> Share with Friends
          </Button>
          <p className="text-white/70 text-[10px] mt-2">Your friends get 50 MathCoins too!</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white border-2 border-[#E5DACE] rounded-xl p-3 text-center">
            <Users size={18} className="mx-auto text-[#8338EC] mb-1" />
            <p className="text-xl font-black text-[#1B4F8A]">{stats.referred_count || 0}</p>
            <p className="text-[10px] font-bold text-slate-400">Friends Referred</p>
          </div>
          <div className="bg-white border-2 border-[#E5DACE] rounded-xl p-3 text-center">
            <Coins size={18} className="mx-auto text-[#FFD23F] mb-1" />
            <p className="text-xl font-black text-[#1B4F8A]">{stats.coins_earned || 0}</p>
            <p className="text-[10px] font-bold text-slate-400">Coins Earned</p>
          </div>
        </div>

        {/* Milestones Progress */}
        {milestones.length > 0 && (
          <div className="bg-white border-2 border-[#E5DACE] rounded-2xl p-4 space-y-3" data-testid="referral-milestones">
            <div className="flex items-center gap-2">
              <Target size={16} className="text-[#FF6B35]" />
              <span className="text-sm font-bold text-[#1B4F8A]">Referral Milestones</span>
            </div>

            {/* Progress bar to next tier */}
            {nextTier && (
              <div>
                <div className="flex justify-between text-[10px] font-bold mb-1">
                  <span className="text-[#8B7355]">{referredCount} referral{referredCount !== 1 ? 's' : ''}</span>
                  <span style={{ color: nextTier.color }}>{nextTier.label} ({nextTier.required} needed)</span>
                </div>
                <div className="h-3 bg-[#F0E6D8] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${progressToNext}%`, background: `linear-gradient(90deg, #FF6B35, ${nextTier.color})` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {nextTier.required - referredCount} more to unlock {nextTier.label} (+{nextTier.reward} coins)
                </p>
              </div>
            )}
            {!nextTier && currentTier && (
              <p className="text-xs font-bold text-[#06D6A0]">You've reached the highest tier!</p>
            )}

            {/* Tier cards */}
            <div className="space-y-2">
              {milestones.map(m => {
                const TierIcon = TIER_ICONS[m.tier] || Star;
                return (
                  <div key={m.tier} data-testid={`milestone-${m.tier}`}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-all ${
                      m.achieved ? 'border-[#06D6A0] bg-[#E5FFF5]' : 'border-[#E5DACE] bg-white opacity-60'}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: m.achieved ? m.color : '#E5DACE' }}>
                      <TierIcon size={14} color="white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold" style={{ color: m.achieved ? '#1B4F8A' : '#8B7355' }}>
                        {m.label}
                      </p>
                      <p className="text-[10px] text-slate-400">{m.required} referrals = +{m.reward} coins</p>
                    </div>
                    {m.achieved ? (
                      <Check size={16} className="text-[#06D6A0]" />
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400">{m.required - referredCount} to go</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Apply code */}
        <div className="bg-white border-2 border-[#E5DACE] rounded-2xl p-4" data-testid="apply-referral-section">
          <p className="text-sm font-bold text-[#1B4F8A] mb-2">Have a friend's code?</p>
          <div className="flex gap-2">
            <Input data-testid="apply-referral-input" value={inputCode} onChange={e => setInputCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code" className="flex-1 font-bold tracking-wider rounded-xl border-[#E5DACE]" />
            <Button data-testid="apply-referral-btn" onClick={applyCode} disabled={loading}
              className="bg-[#06D6A0] hover:bg-[#04A77C] text-white font-bold rounded-full px-4">Apply</Button>
          </div>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white border-2 border-[#E5DACE] rounded-2xl p-4" data-testid="referral-leaderboard">
            <p className="text-sm font-bold text-[#1B4F8A] mb-3 flex items-center gap-2"><Trophy size={16} color="#FFD23F" /> Top Referrers</p>

            {/* Top 3 podium */}
            {leaderboard.length >= 3 && (
              <div className="flex items-end justify-center gap-3 mb-4 py-2">
                {[1, 0, 2].map(i => {
                  const r = leaderboard[i];
                  if (!r) return null;
                  const height = i === 0 ? 'h-20' : i === 1 ? 'h-16' : 'h-12';
                  const colors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  const positions = [1, 2, 3];
                  const pos = positions[[1, 0, 2].indexOf(i)];
                  return (
                    <div key={i} className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold mb-1"
                        style={{ background: colors[pos - 1] }}>
                        {pos}
                      </div>
                      <p className="text-[10px] font-bold text-[#1B4F8A] text-center truncate w-16">{r.name}</p>
                      <p className="text-[9px] text-[#8338EC] font-bold">{r.referred_count}</p>
                      <div className={`w-14 ${height} rounded-t-lg mt-1`}
                        style={{ background: `${colors[pos - 1]}33` }} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rest of leaderboard */}
            <div className="space-y-1">
              {leaderboard.slice(leaderboard.length >= 3 ? 3 : 0).map((r, i) => (
                <div key={r.id || i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#FFFBF5]"
                  data-testid={`leaderboard-entry-${i}`}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold bg-[#F0E6D8] text-[#8B7355]">
                    {(leaderboard.length >= 3 ? i + 4 : i + 1)}
                  </span>
                  <span className="flex-1 text-xs font-bold text-[#1B4F8A]">{r.name}</span>
                  <span className="text-[10px] font-bold text-[#8338EC]">{r.referred_count} refs</span>
                  <span className="text-[10px] font-bold text-[#FFD23F]">{r.coins_earned}c</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <BottomNav active="profile" />
    </div>
  );
}
